import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toPoint, forward as toMGRS } from "mgrs";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import GpsFixedRoundedIcon from "@mui/icons-material/GpsFixedRounded";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import SendIcon from "@mui/icons-material/Send";
import { BACKEND_URL } from "../utils/constants";
import {
  tacticalActionSx,
  tacticalChipSx,
  tacticalSurface,
} from "../utils/fdcTheme";

function parseMgrs(raw) {
  const s = (raw || "").trim();
  if (!s) return null;
  const m = s.match(/^([0-9]{1,2}[C-HJ-NP-X][A-Z]{2}[0-9]{2,})/i);
  if (!m) return null;
  const mgrs = m[1].toUpperCase().replace(/\s+/g, "");
  const digits = mgrs.replace(/^[0-9]{1,2}[C-HJ-NP-X][A-Z]{2}/i, "");
  if (digits.length % 2 !== 0) return null;
  try {
    const [lon, lat] = toPoint(mgrs);
    return { mgrs, lat, lon };
  } catch {
    return null;
  }
}

export default function GunPositionPage() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [gunNumber] = useState(() => {
    try {
      return localStorage.getItem("guncmd_myGun") || "";
    } catch {
      return "";
    }
  });

  const [mgrsInput, setMgrsInput] = useState("");
  const [altitude, setAltitude] = useState("");
  const [parsed, setParsed] = useState(null);
  const [error, setError] = useState("");
  const [gpsBusy, setGpsBusy] = useState(false);
  const [gpsMsg, setGpsMsg] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [existingPosition, setExistingPosition] = useState(null);

  // Redirect if no gun selected
  useEffect(() => {
    if (!gunNumber) navigate("/guncmd/setup", { replace: true });
  }, [gunNumber, navigate]);

  // Check if position already exists
  useEffect(() => {
    if (!gunNumber) return;
    axios
      .get(`${BACKEND_URL}/latest-gun-positions`, { timeout: 10000 })
      .then((res) => {
        const guns = res.data?.data || [];
        const mine = guns.find((g) => String(g.gun_number) === String(gunNumber));
        if (mine) setExistingPosition(mine);
      })
      .catch(() => {});
  }, [gunNumber]);

  const handleMgrsBlur = useCallback(() => {
    const p = parseMgrs(mgrsInput);
    setParsed(p);
    if (mgrsInput.trim() && !p) setError("Invalid MGRS. Example: 29SND6264358593");
    else setError("");
  }, [mgrsInput]);

  const handleGps = () => {
    if (!navigator?.geolocation) {
      setGpsMsg("Geolocation not supported.");
      return;
    }
    setGpsBusy(true);
    setGpsMsg("Getting GPS fix...");
    setError("");

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        try {
          const { latitude: lat, longitude: lon, altitude: alt, accuracy: acc } = pos.coords;
          const mgrs = toMGRS([lon, lat], 5);
          setMgrsInput(mgrs);
          setParsed({ mgrs, lat, lon });
          if (typeof alt === "number") setAltitude(alt.toFixed(2));
          setGpsMsg(`GPS OK${acc != null ? ` (${Math.round(acc)}m)` : ""}`);
        } catch {
          setGpsMsg("Failed to convert GPS.");
        } finally {
          setGpsBusy(false);
        }
      },
      (err) => {
        setGpsBusy(false);
        if (err?.code === 1) setGpsMsg("Permission denied.");
        else if (err?.code === 3) setGpsMsg("GPS timeout. Try again.");
        else setGpsMsg("GPS error. Try again.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const handleSubmit = async () => {
    setError("");
    const p = parsed || parseMgrs(mgrsInput);
    if (!p) {
      setError("Invalid MGRS coordinates.");
      return;
    }
    const alt = parseFloat((altitude || "").replace(",", "."));
    if (!Number.isFinite(alt) || alt < -500 || alt > 9000) {
      setError("Altitude must be between -500 and 9000 m.");
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/latest-gun-position`, {
        gun_number: gunNumber,
        Norths: Number(p.lat.toFixed(7)),
        wests: Number(p.lon.toFixed(7)),
        altitude: Number(alt.toFixed(2)),
      });
      setSubmitted(true);
      setTimeout(() => navigate("/guncmd"), 600);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to submit position.");
    }
  };

  const handleSkip = () => navigate("/guncmd");

  const canSubmit = parsed && altitude.trim() && !error;

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 6 } }}>
      <Box
        sx={{
          ...tacticalSurface(theme, true),
          borderRadius: 3,
          p: { xs: 2.5, sm: 3.5 },
        }}
      >
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <GpsFixedRoundedIcon sx={{ fontSize: 32, color: "text.secondary" }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Gun Position
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
                <Chip
                  size="small"
                  label={`Gun ${gunNumber}`}
                  sx={tacticalChipSx(theme, "neutral")}
                />
                {existingPosition && (
                  <Chip
                    size="small"
                    label="Has saved position"
                    sx={tacticalChipSx(theme, "success")}
                  />
                )}
              </Stack>
            </Box>
          </Stack>

          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Enter your gun's position using MGRS coordinates and altitude, or
            use GPS to auto-fill from your device.
          </Typography>

          {error && <Alert severity="error" sx={{ fontWeight: 700 }}>{error}</Alert>}
          {gpsMsg && (
            <Alert severity={gpsMsg.startsWith("GPS OK") ? "success" : "info"} sx={{ fontWeight: 700 }}>
              {gpsMsg}
            </Alert>
          )}
          {submitted && (
            <Alert severity="success" sx={{ fontWeight: 700 }}>
              Position saved! Redirecting...
            </Alert>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              fullWidth
              label="MGRS Coordinates"
              placeholder="29SND6264358593"
              value={mgrsInput}
              onChange={(e) => {
                setMgrsInput(e.target.value);
                setError("");
                setParsed(null);
              }}
              onBlur={handleMgrsBlur}
              error={!!error && error.includes("MGRS")}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0),
                },
              }}
            />
            <Button
              variant="outlined"
              disabled={gpsBusy}
              onClick={handleGps}
              startIcon={<MyLocationIcon />}
              sx={{
                ...tacticalActionSx(theme, "warning"),
                minWidth: { xs: "100%", sm: 140 },
              }}
            >
              {gpsBusy ? "Acquiring..." : "Use GPS"}
            </Button>
          </Stack>

          <TextField
            fullWidth
            label="Altitude (m)"
            placeholder="167.53"
            value={altitude}
            onChange={(e) => {
              setAltitude(e.target.value);
              setError("");
            }}
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0),
              },
            }}
          />

          {parsed && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Parsed: {parsed.mgrs} ({parsed.lat.toFixed(5)}, {parsed.lon.toFixed(5)})
            </Typography>
          )}

          <Stack spacing={1.5}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={!canSubmit || submitted}
              onClick={handleSubmit}
              endIcon={<SendIcon />}
              sx={{
                ...tacticalActionSx(theme, "success", "contained"),
                py: 1.5,
                fontSize: 15,
                fontWeight: 700,
              }}
            >
              Submit Position & Continue
            </Button>

            {existingPosition && (
              <Button
                fullWidth
                variant="outlined"
                onClick={handleSkip}
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  ...tacticalActionSx(theme),
                  fontWeight: 700,
                }}
              >
                Skip — use saved position
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </Container>
  );
}
